import React, { useState, useEffect, useRef } from "react";
import { event } from "../services/events";
import { Link } from "react-router-dom";
async function api(url, body) {
  const r = await fetch("/api/" + url, {
    method: body ? "POST" : "GET",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json();
  if (!r.ok) throw Error(j.message || "No se pudo completar la petición");
  return j;
}
function Address({ name, value, onChange }) {
  const [rows, setRows] = useState([]),
    [message, setMessage] = useState(""),
    [index, setIndex] = useState(-1),
    [loading, setLoading] = useState(false);
  const sequence = useRef(0);
  useEffect(() => {
    const n = ++sequence.current;
    if (value.token || value.label.length < 4) {
      setRows([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const j = await api(
          "places/search?q=" + encodeURIComponent(value.label),
        );
        if (n !== sequence.current) return;
        setRows(j.suggestions);
        setMessage(
          j.suggestions.length
            ? ""
            : "No encontramos esa dirección. Añade municipio y número, o solicita valoración personal.",
        );
      } catch (e) {
        if (n === sequence.current) setMessage(e.message);
      } finally {
        if (n === sequence.current) setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value.label, value.token]);
  async function select(row) {
    ++sequence.current;
    setRows([]);
    setLoading(true);
    try {
      const a = await api("places/resolve", { token: row.token });
      onChange({ ...value, ...a });
      setMessage(
        "Dirección española verificada" +
          (a.precision === "street"
            ? ". Añade el número en observaciones."
            : ""),
      );
    } catch (e) {
      setMessage(e.message);
    } finally {
      setLoading(false);
    }
  }
  return (
    <div className="address">
      <label htmlFor={"address-" + name}>
        {name === "origin" ? "Dirección de origen" : "Dirección de destino"}
        <span>Calle, número y municipio · solo España</span>
      </label>
      <input
        id={"address-" + name}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={rows.length > 0}
        aria-controls={"list-" + name}
        aria-activedescendant={
          index >= 0 ? "option-" + name + "-" + index : undefined
        }
        autoComplete="off"
        value={value.label}
        onChange={(e) => {
          onChange({ ...value, label: e.target.value, token: null });
          setIndex(-1);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setIndex((i) => Math.min(i + 1, rows.length - 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setIndex((i) => Math.max(0, i - 1));
          }
          if (e.key === "Escape") setRows([]);
          if (e.key === "Enter" && index >= 0 && rows[index]) {
            e.preventDefault();
            select(rows[index]);
          }
        }}
      />
      {rows.length > 0 && (
        <ul role="listbox" id={"list-" + name}>
          {rows.map((row, i) => (
            <li
              role="option"
              id={"option-" + name + "-" + i}
              aria-selected={i === index}
              key={row.token}
            >
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(row)}
              >
                {row.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <small role="status">{loading ? "Buscando…" : message}</small>
    </div>
  );
}
const fresh = () => ({
  label: "",
  floor: null,
  usableLift: null,
  parking: "unknown",
});
export default function Estimator() {
  const [step, setStep] = useState(0),
    [form, setForm] = useState({
      origin: fresh(),
      destination: fresh(),
      belongings: {mode: "list", items: {}, boxBand: "unknown", other: ""},
      moveType: "home",
      packing: "none",
      assemblyPieces: 0,
      assemblyUnknown: false,
      specialItems: false,
      needsCrane: false,
      unpricedCosts: false,
      storage: false,
      date: "",

      spanishDeclaration: false,
    }),
    [quote, setQuote] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [done, setDone] = useState(null);
  const heading = useRef();
  const furniture = [["sofa", "Sofás"], ["bed", "Camas con colchón"], ["wardrobe", "Armarios"], ["drawers", "Cómodas / cajoneras"], ["table", "Mesas / escritorios"], ["chair", "Sillas"], ["appliance", "Electrodomésticos grandes"], ["tvUnit", "Muebles de TV"], ["shelf", "Estanterías"]];
  const belongings = (patch) => set("belongings", {...form.belongings, ...patch});
  const count = (key, delta) => belongings({items: {...form.belongings.items, [key]: Math.min(30, Math.max(0, (form.belongings.items[key] || 0) + delta))}});
  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setQuote(null);
  };
  function next(n) {
    if(n===1 && step===0 && (!form.origin.label.trim() || !form.destination.label.trim())) {
      setError("Escribe desde dónde y hasta dónde quieres hacer la mudanza.");return;
    }
    if (n === 1 && step === 0) event("inicio");
    if (n > step) event(["inicio", "trayecto", "carga", "accesos"][n]);
    setError("");
    setStep(n);
    setTimeout(() => heading.current?.focus(), 0);
  }
  async function calculate() {
    if (!form.origin.label.trim() || !form.destination.label.trim()) {
      setError("Completa las dos direcciones.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const params = new URLSearchParams(window.location.search),
        channel =
          params.get("utm_source") === "google" &&
          params.get("utm_medium") === "organic"
            ? "gbp"
            : params.get("utm_source") === "blog"
              ? "blog"
              : document.referrer.includes("/blog/")
                ? "blog"
                : document.referrer.includes("google.")
                  ? "organic"
                  : "direct";
      const result = await api("estimaciones", { ...form, channel });
      setQuote(result);
      event(result.result.status === "range" ? "resultado" : "revision");
      next(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = new FormData(e.currentTarget);
      if (!quote) throw Error("Calcula primero la estimación");
      data.set("telefono", String(data.get("telefono") || "").trim());
      data.set("email", String(data.get("email") || "").trim());
      if(!data.get("telefono") && !data.get("email")) throw Error("Déjanos un teléfono o un correo para poder responderte.");
      data.set("estimateToken", quote.token);
      data.set("aceptaLOPD", "true");
      const r = await fetch("/api/presupuesto", { method: "POST", body: data });
      const j = await r.json();
      if (!r.ok) throw Error(j.message || "No se pudo enviar");
      setDone(j);
      event("solicitud");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="wrap calculator">
      <p className="eyebrow">TU MUDANZA, PASO A PASO</p>
      <h1 ref={heading} tabIndex={-1}>
        ¿Cuánto puede costar tu mudanza?
      </h1>
      <p className="intro">
        Obtén una orientación antes de dejar tus datos. Después, Britosa
        revisará contigo los muebles, los accesos y la fecha. Solo tienes que
        decirnos qué te llevas; no necesitas medir tu casa.
      </p>
      <ol className="steps">
        {["Dónde", "Qué llevas", "Acceso", "Tu precio"].map((s, i) => (
          <li key={s} aria-current={step === i ? "step" : undefined}>
            <button type="button" disabled={i > step} onClick={() => next(i)}>
              <b>{i + 1}</b>
              {s}
            </button>
          </li>
        ))}
      </ol>
      <div className="calc-card">
        {step === 0 && (
          <>
            <h2>¿De dónde a dónde?</h2>
            <div className="grid two">
              {["origin", "destination"].map((k) => (
                <Address
                  key={k}
                  name={k}
                  value={form[k]}
                  onChange={(v) => set(k, v)}
                />
              ))}
            </div>
            {(!form.origin.token || !form.destination.token) && (
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.spanishDeclaration}
                  onChange={(e) => set("spanishDeclaration", e.target.checked)}
                />
                Confirmo que origen y destino están en España. Las direcciones
                escritas sin seleccionar se revisarán personalmente.
              </label>
            )}
            <p className="fine">
              Direcciones:{" "}
              <a
                href="https://www.cartociudad.es/web/portal"
                target="_blank"
                rel="noreferrer"
              >
                CartoCiudad · IGN/CNIG
              </a>
              . Islas, Ceuta y Melilla se admiten y requieren valoración
              personal. Si no encuentras una dirección, escríbela completa;
              podrás enviar la solicitud.
            </p>
            <div className="grid two">
              <label>
                Fecha prevista
                <input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.date}
                  onChange={(e) => set("date", e.target.value)}
                />
                <small>Puedes dejarla abierta si todavía no la sabes.</small>
              </label>

            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2>¿Qué te llevas?</h2>
            <p>Marca los muebles y una cantidad aproximada de cajas. Puedes cambiarlo después.</p>
            <div className="belonging-mode" role="group" aria-label="Cómo indicar lo que llevas">
              <button type="button" aria-pressed={form.belongings.mode === "list"} onClick={() => belongings({mode: "list"})}>Elegir muebles y cajas</button>
              <button type="button" aria-pressed={form.belongings.mode === "help"} onClick={() => belongings({mode: "help"})}>Prefiero enviar fotos o hablarlo</button>
            </div>
            {form.belongings.mode === "list" ? <>
              <div className="belonging-grid">
                {furniture.map(([key, label]) => <div className="belonging-item" key={key}>
                  <span id={"item-" + key}>{label}</span>
                  <div className="counter" role="group" aria-labelledby={"item-" + key}>
                    <button type="button" aria-label={"Quitar " + label.toLowerCase()} disabled={!form.belongings.items[key]} onClick={() => count(key, -1)}>−</button>
                    <output aria-live="polite" aria-label={"Cantidad de " + label.toLowerCase()}>{form.belongings.items[key] || 0}</output>
                    <button type="button" aria-label={"Añadir " + label.toLowerCase()} disabled={form.belongings.items[key] >= 30} onClick={() => count(key, 1)}>+</button>
                  </div>
                </div>)}
              </div>
              <label htmlFor="box-band">¿Cuántas cajas, aproximadamente?
                <select id="box-band" value={form.belongings.boxBand} onChange={e => belongings({boxBand: e.target.value})}>
                  <option value="unknown">Todavía no lo sé</option><option value="none">No llevo cajas</option><option value="1-10">Entre 1 y 10</option><option value="11-20">Entre 11 y 20</option><option value="21-40">Entre 21 y 40</option><option value="41-60">Entre 41 y 60</option><option value="more">Más de 60</option>
                </select>
              </label>
              <details><summary>¿Llevas algo más?</summary><label>Otros muebles u objetos (opcional)<textarea rows="2" maxLength="1000" value={form.belongings.other} placeholder="Por ejemplo: bicicleta, plantas o muebles de terraza" onChange={e => belongings({other: e.target.value})}/></label></details>
              <p className="fine">Con esto calculamos una orientación. Si faltan cajas u otros objetos, lo revisamos contigo.</p>
            </> : <p className="notice">Perfecto. Al terminar puedes añadir fotos y tu teléfono. Te ayudaremos a preparar el presupuesto sin contar ni medir nada.</p>}
            <details className="optional-services"><summary>¿Necesitas algún servicio más?</summary>
              <div className="grid two">
                <label>Preparar y embalar tus cosas<select value={form.packing} onChange={e => set("packing", e.target.value)}><option value="none">Lo preparo yo</option><option value="full">Quiero que lo prepare Britosa</option><option value="partial">Necesito ayuda con parte / no lo sé</option></select></label>
                <label>¿Cuántos muebles necesitan desmontaje y montaje?<select value={form.assemblyUnknown ? "unknown" : String(form.assemblyPieces)} onChange={e => {set("assemblyUnknown", e.target.value === "unknown");set("assemblyPieces", e.target.value === "unknown" ? 0 : Number(e.target.value));}}><option value="0">Ninguno</option>{[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}<option value="unknown">No lo sé, lo vemos juntos</option></select></label>
              </div>
              {[["specialItems", "Llevo algo muy pesado o delicado (piano, caja fuerte…)"], ["storage", "Necesito guardar mis muebles un tiempo"]].map(([k,t]) => <label className="check" key={k}><input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)}/>{t}</label>)}
              <label className="check"><input type="checkbox" checked={form.moveType === "office"} onChange={e=>set("moveType", e.target.checked ? "office" : "home")}/>Es una oficina o un negocio</label>
            </details>
          </>
        )}
        {step === 2 && (
          <>
            <h2>¿Cómo son los accesos?</h2>
            <div className="grid two">
              {["origin", "destination"].map((k) => (
                <fieldset key={k}>
                  <legend>{k === "origin" ? "Origen" : "Destino"}</legend>
                  <p>{form[k].label || "Dirección pendiente"}</p>
                  <label>
                    Planta
                    <select
                      value={form[k].floor ?? ""}
                      onChange={(e) =>
                        set(k, {
                          ...form[k],
                          floor:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">No lo sé</option>
                      {Array.from({ length: 13 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? "Bajo / a pie de calle" : i}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    ¿Hay ascensor para subir los muebles?
                    <select
                      value={
                        form[k].usableLift === null
                          ? ""
                          : String(form[k].usableLift)
                      }
                      onChange={(e) =>
                        set(k, {
                          ...form[k],
                          usableLift:
                            e.target.value === ""
                              ? null
                              : e.target.value === "true",
                        })
                      }
                    >
                      <option value="true">Sí, creo que caben / entrada a pie de calle</option>
                      <option value="false">No / algunos muebles van por escalera</option>
                      <option value="">No lo sé</option>
                    </select>
                  </label>
                  <label>¿Se puede aparcar cerca de la puerta?
                    <select value={form[k].parking} onChange={e=>set(k,{...form[k],parking:e.target.value})}>
                      <option value="unknown">No lo sé</option><option value="door">Sí, junto al portal o la puerta</option><option value="near">En la misma calle, un poco más lejos</option><option value="difficult">Está lejos o el acceso es complicado</option>
                    </select>
                  </label>
                </fieldset>
              ))}
            </div>
            <p className="fine">
              Comprueba el espacio para el vehículo y las restricciones en ambos
              domicilios. El precio final depende también de estos accesos.
            </p>
          </>
        )}
        {step === 3 && quote && (
          <>
            <p className="eyebrow">{quote.reference}</p>
            {quote.result.status === "range" ? (
              <>
                <h2>Tu estimación orientativa</h2>
                <p className="price">
                  {quote.result.gross[0].toLocaleString("es-ES")}–
                  {quote.result.gross[1].toLocaleString("es-ES")} €
                </p>
                <p>
                  IVA {Math.round(quote.result.taxRate * 100)}% incluido ·{" "}
                  {quote.route.roadKm} km por carretera
                </p>
                <p className="fine">Calculado a partir de los muebles y cajas que has indicado. Lo confirmaremos contigo.</p>
                <details>
                  <summary>Ver el desglose</summary>
                  <dl>
                    {Object.entries(quote.result.components)
                      .filter(([, v]) => v[1] > 0)
                      .map(([k, v]) => (
                        <div key={k}>
                          <dt>{k}</dt>
                          <dd>
                            {v[0].toFixed(2)}–{v[1].toFixed(2)} € sin IVA
                          </dd>
                        </div>
                      ))}
                  </dl>
                </details>
                <p className="notice">
                  {quote.result.source === "market"
                    ? "Orientación con tarifas provisionales de mercado; todavía no son las tarifas confirmadas de Britosa."
                    : "Estimación con las reglas de Britosa."}{" "}
                  No es una reserva ni un presupuesto cerrado. Se confirmará
                  tras revisar inventario, accesos, fecha y costes necesarios.
                </p>
              </>
            ) : (
              <>
                <h2>Esta mudanza necesita una valoración personal</h2>
                <p>No necesitas completar más datos ahora. Déjanos tu teléfono y, si quieres, unas fotos. Te ayudamos a revisar:</p>
                <ul>
                  {quote.result.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </>
            )}
            {quote.route.roadKm != null && (
              <small>
                Ruta orientativa: OSRM / ©{" "}
                <a
                  href="https://www.openstreetmap.org/copyright"
                  target="_blank"
                  rel="noreferrer"
                >
                  OpenStreetMap contributors
                </a>
                . No incorpora restricciones específicas para camiones.
              </small>
            )}
            <hr />
            {done ? (
              <div role="status">
                <h2>Solicitud recibida</h2>
                <p>
                  Referencia: <strong>{done.reference}</strong>. Britosa se
                  pondrá en contacto contigo para revisar los detalles.
                </p>
                <a className="button" href="tel:+34691604880">
                  Llamar al 691 60 48 80
                </a>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h2>¿Te llamamos para concretarlo?</h2>
                <p>
                  Puedes añadir fotos de los muebles y accesos. Evita
                  documentos, rostros u otros datos personales.
                </p>
                <div className="grid two">
                  <label>
                    Nombre
                    <input
                      name="nombre"
                      required
                      maxLength="100"
                      autoComplete="given-name"
                    />
                  </label>
                  <label>
                    Teléfono
                    <input name="telefono" type="tel" autoComplete="tel" />
                  </label>
                  <label>
                    Correo electrónico (opcional si indicas teléfono)
                    <input name="email" type="email" autoComplete="email" />
                  </label>
                </div>
                <small>
                  Necesitamos un teléfono o un correo para responderte.
                </small>
                <label>
                  ¿Algo más que debamos saber? (opcional)
                  <textarea
                    name="descripcion"
                    rows="3"
                    maxLength="4000"
                    placeholder="Número de portal, fecha flexible, condiciones especiales…"
                  />
                </label>
                <label>
                  Fotografías opcionales
                  <input
                    name="fotos"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                  />
                  <small>
                    Hasta 8 imágenes JPG, PNG o WebP · 8 MB por imagen. Solo las verá Britosa.
                  </small>
                </label>
                <label className="honeypot" aria-hidden="true">
                  Web
                  <input name="website" tabIndex={-1} autoComplete="off" />
                </label>
                <label className="check">
                  <input type="checkbox" required />
                  <span>He leído la <Link to="/politica-privacidad">política de privacidad</Link> para gestionar mi solicitud.</span>
                </label>
                <button className="button" disabled={busy}>
                  {busy ? "Enviando…" : "Pedir presupuesto"}
                </button>
              </form>
            )}
          </>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {step < 3 && (
          <div className="actions">
            {step > 0 && (
              <button type="button" onClick={() => next(step - 1)}>
                Atrás
              </button>
            )}
            <button
              className="button"
              type="button"
              disabled={busy}
              onClick={() => (step === 2 ? calculate() : next(step + 1))}
            >
              {busy
                ? "Calculando ruta y precio…"
                : step === 2
                  ? "Ver orientación de precio"
                  : "Continuar"}
            </button>
          </div>
        )}
        {step === 3 && !done && (
          <button type="button" onClick={() => next(0)}>
            Editar la mudanza
          </button>
        )}
      </div>
    </section>
  );
}
